import { Person } from "@/types";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { GET_EDUCATION_CERTIFICATION_HISTORY_QUERY } from "./queries";
import { toFormat } from "@/lib/dateHelpers";
import MetricGrid from "@/components/MetricGrid";
import TabLayout from "./_TabLayout";
import { usePersonEducationAccess } from "../person/_usePersonEducationAccess";
import LifelongLearningTable from "@/features/person/components/LifelongLearningTable";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";

type PersonData = { person: Person };

export default function LearningDetails() {
  const { pgaId } = useParams();
  const { loaded, canViewPersonEducation } = usePersonEducationAccess();
  const canManageEducationRequirements = useHasCapabilityBool({
    domain: DOMAIN.EDUCATION,
    permission: PERMISSION.manageEducationPrograms
  });
  const canDeleteEducationProgramLevels = useHasCapabilityBool({
    domain: DOMAIN.EDUCATION,
    permission: PERMISSION.deleteEducationProgramLevels
  });
  const flags = useFlags();
  const includeAllEnrollments = canManageEducationRequirements && !!flags["backoffice-manage-lifelong-learning"];

  const { data, loading, error } = useQuery<PersonData>(GET_EDUCATION_CERTIFICATION_HISTORY_QUERY, {
    variables: {
      pgaId,
      includeAllEnrollments
    },
    fetchPolicy: "cache-first",
    skip: !loaded || !canViewPersonEducation || !pgaId
  });

  if (!loaded) {
    return null;
  }

  if (error) return <div>Error: {error.message}</div>;

  const personData = data?.person;
  const certificationHistory = personData?.educationCertificationHistory;
  const latestCertification = personData?.educationLatestCertification;

  const metrics = latestCertification
    ? [
        {
          label: "Certification Program Type",
          value: latestCertification.programName
        },
        {
          label: "Career Path",
          value: latestCertification.careerPath
        },
        {
          label: "Start Date",
          value: toFormat(latestCertification.startDate)
        },
        {
          label: "Completion Date",
          value: latestCertification.endDate && toFormat(latestCertification.endDate)
        },
        {
          label: "Expiration Date",
          value: latestCertification.subscription?.endDate && toFormat(latestCertification.subscription.endDate)
        }
      ]
    : [];

  return (
    <TabLayout>
      {!!metrics.length && <MetricGrid metrics={metrics} layout="expanded" />}
      <div className="pt-12" />
      <LifelongLearningTable
        personId={pgaId}
        certificationHistory={certificationHistory ?? []}
        loading={loading}
        canManageEducationRequirements={canManageEducationRequirements}
        canDeleteEducationProgramLevels={canDeleteEducationProgramLevels}
      />
    </TabLayout>
  );
}
